import {
  RESOURCE_TYPES,
  SYNCED_RESOURCE_TYPES,
  emptyResource,
  type JsonValue,
  type RemoteResources,
  type ResourceDocument,
  type ResourceType,
  type SyncOperation
} from '../domain/resources';
import { applyJsonChange, cloneJson, diffJson } from './json-patch';
import type { LocalDatabase } from './local-database';

type UnknownJson = JsonValue | undefined;

function operationId(): string {
  return globalThis.crypto?.randomUUID?.()
    || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function safeClone<T>(value: T): T {
  if (value === undefined) return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

export class RepositoryStore {
  private resources = new Map<ResourceType, ResourceDocument>();
  private writeQueue: Promise<void> = Promise.resolve();
  private localOrder = 0;

  constructor(
    private readonly database: LocalDatabase,
    initialResources: ResourceDocument[]
  ) {
    initialResources.forEach(resource => this.resources.set(resource.type, safeClone(resource)));
    RESOURCE_TYPES.forEach(type => {
      if (!this.resources.has(type)) this.resources.set(type, emptyResource(type));
    });
  }

  read<T extends JsonValue>(type: ResourceType, key: string, fallback: T): T {
    const value = this.resources.get(type)?.payload[key];
    return value === undefined ? safeClone(fallback) : safeClone(value as T);
  }

  readOptional<T extends JsonValue>(type: ResourceType, key: string): T | null {
    const value = this.resources.get(type)?.payload[key];
    return value === undefined ? null : safeClone(value as T);
  }

  write(type: ResourceType, key: string, value: JsonValue): void {
    const resource = safeClone(this.resources.get(type) || emptyResource(type));
    const previous = resource.payload[key];
    const changes = diffJson(previous, value);
    if (changes.length === 0) return;

    resource.payload[key] = cloneJson(value);
    resource.localRevision += 1;
    resource.updatedAt = new Date().toISOString();
    this.resources.set(type, resource);

    const operations = SYNCED_RESOURCE_TYPES.has(type)
      ? changes.map(change => ({
          id: operationId(),
          resourceType: type,
          documentKey: key,
          path: change.path,
          action: change.action,
          ...(change.value === undefined ? {} : { value: change.value }),
          createdAt: resource.updatedAt,
          localOrder: this.nextLocalOrder()
        } satisfies SyncOperation))
      : [];

    this.enqueue(async () => {
      await this.database.commit(resource, operations);
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:repository-change', {
        detail: { resourceType: type, documentKey: key }
      }));
    }
  }

  remove(type: ResourceType, key: string): void {
    const resource = safeClone(this.resources.get(type) || emptyResource(type));
    if (!Object.hasOwn(resource.payload, key)) return;
    delete resource.payload[key];
    resource.localRevision += 1;
    resource.updatedAt = new Date().toISOString();
    this.resources.set(type, resource);
    const operation: SyncOperation = {
      id: operationId(),
      resourceType: type,
      documentKey: key,
      path: [],
      action: 'delete',
      createdAt: resource.updatedAt,
      localOrder: this.nextLocalOrder()
    };
    this.enqueue(async () => {
      await this.database.commit(
        resource,
        SYNCED_RESOURCE_TYPES.has(type) ? [operation] : []
      );
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:repository-change', {
        detail: { resourceType: type, documentKey: key }
      }));
    }
  }

  async pendingOperations(): Promise<SyncOperation[]> {
    await this.flush();
    return this.database.listOperations();
  }

  async acknowledgeOperations(ids: string[]): Promise<void> {
    await this.database.deleteOperations(ids);
  }

  async applyRemoteResources(remote: RemoteResources): Promise<void> {
    await this.flush();
    const pending = await this.database.listOperations();
    const pendingByType = new Map<ResourceType, SyncOperation[]>();
    pending.forEach(operation => {
      const list = pendingByType.get(operation.resourceType) || [];
      list.push(operation);
      pendingByType.set(operation.resourceType, list);
    });

    let changed = false;
    for (const type of RESOURCE_TYPES) {
      const remoteResource = remote[type];
      if (!remoteResource || type === 'cache') continue;
      const local = safeClone(this.resources.get(type) || emptyResource(type));
      const payload = safeClone(remoteResource.payload || {});

      for (const operation of pendingByType.get(type) || []) {
        const current = payload[operation.documentKey];
        const next = applyJsonChange(current, operation);
        if (next === undefined) delete payload[operation.documentKey];
        else payload[operation.documentKey] = next;
      }

      changed ||= JSON.stringify(local.payload) !== JSON.stringify(payload);
      const merged: ResourceDocument = {
        ...local,
        remoteVersion: Number(remoteResource.version || 0),
        updatedAt: remoteResource.updatedAt || local.updatedAt,
        payload
      };
      this.resources.set(type, merged);
      await this.database.putResource(merged);
    }
    if (changed && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:resources-loaded'));
    }
  }

  async adoptAccount(remote: RemoteResources): Promise<void> {
    await this.flush();
    await this.database.setMetadata(`account-local-archive:${Date.now()}`, JSON.stringify(this.snapshot()));
    await this.database.deleteOperations((await this.database.listOperations()).map(operation => operation.id));
    const complete: RemoteResources = {};
    for (const type of SYNCED_RESOURCE_TYPES) {
      complete[type] = remote[type] || { payload: {}, updatedAt: null, version: 0 };
    }
    await this.applyRemoteResources(complete);
    const cache = emptyResource('cache');
    this.resources.set('cache', cache);
    await this.database.putResource(cache);
  }

  async flush(): Promise<void> {
    await this.writeQueue;
    await this.database.flush();
  }

  snapshot(): ResourceDocument[] {
    return [...this.resources.values()].map(resource => safeClone(resource));
  }

  private enqueue(task: () => Promise<void>): void {
    this.writeQueue = this.writeQueue.then(task).catch(error => {
      console.error('[repository] Persistence failed:', error);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app:persistence-error', { detail: { error } }));
      }
    });
  }

  private nextLocalOrder(): number {
    this.localOrder = (this.localOrder + 1) % 1000;
    return Date.now() * 1000 + this.localOrder;
  }
}

class DocumentRepository {
  constructor(
    protected readonly store: RepositoryStore,
    protected readonly resourceType: ResourceType
  ) {}

  protected get<T extends JsonValue>(key: string, fallback: T): T {
    return this.store.read(this.resourceType, key, fallback);
  }

  protected getOptional<T extends JsonValue>(key: string): T | null {
    return this.store.readOptional(this.resourceType, key);
  }

  protected set(key: string, value: JsonValue): void {
    this.store.write(this.resourceType, key, value);
  }

  protected delete(key: string): void {
    this.store.remove(this.resourceType, key);
  }
}

export class ProgressRepository extends DocumentRepository {
  private readonly key = 'jidelnickovac_v1';
  constructor(store: RepositoryStore) { super(store, 'progress'); }
  getState<T extends JsonValue>(fallback: T): T { return this.get(this.key, fallback); }
  saveState(value: JsonValue): void { this.set(this.key, value); }
  clear(): void { this.delete(this.key); }
}

export class TrainingRepository extends DocumentRepository {
  private readonly key = 'jidelnickovac_training_v1';
  constructor(store: RepositoryStore) { super(store, 'training'); }
  getPlan<T extends JsonValue>(fallback: T): T { return this.get(this.key, fallback); }
  savePlan(value: JsonValue): void { this.set(this.key, value); }
}

export class SupplementsRepository extends DocumentRepository {
  private readonly key = 'jidelnickovac_supplements_v1';
  constructor(store: RepositoryStore) { super(store, 'supplements'); }
  getAll<T extends JsonValue>(fallback: T): T { return this.get(this.key, fallback); }
  saveAll(value: JsonValue): void { this.set(this.key, value); }
}

export class ChatRepository extends DocumentRepository {
  constructor(store: RepositoryStore) { super(store, 'chat-sessions'); }
  getSessions<T extends JsonValue>(fallback: T): T {
    return this.get('jidelnickovac_chat_sessions_v1', fallback);
  }
  saveSessions(value: JsonValue): void {
    this.set('jidelnickovac_chat_sessions_v1', value);
  }
  getCoachCache<T extends JsonValue>(): T | null {
    return this.getOptional<T>('jidelnickovac_dashboard_coach_v1');
  }
  saveCoachCache(value: JsonValue): void {
    this.set('jidelnickovac_dashboard_coach_v1', value);
  }
  clearCoachCache(): void {
    this.delete('jidelnickovac_dashboard_coach_v1');
  }
}

export class SettingsRepository extends DocumentRepository {
  constructor(store: RepositoryStore) { super(store, 'settings'); }
  getString(key: string, fallback = ''): string {
    return this.get(key, fallback);
  }
  setString(key: string, value: string): void {
    this.set(key, value);
  }
  remove(key: string): void {
    this.delete(key);
  }
}

export class CacheRepository extends DocumentRepository {
  constructor(store: RepositoryStore) { super(store, 'cache'); }
  getJson<T extends JsonValue>(key: string): T | null {
    return this.getOptional<T>(key);
  }
  setJson(key: string, value: JsonValue): void {
    this.set(key, value);
  }
  remove(key: string): void {
    this.delete(key);
  }
}

export interface AppRepositories {
  progress: ProgressRepository;
  training: TrainingRepository;
  supplements: SupplementsRepository;
  chat: ChatRepository;
  settings: SettingsRepository;
  cache: CacheRepository;
}

export function createRepositories(store: RepositoryStore): AppRepositories {
  return {
    progress: new ProgressRepository(store),
    training: new TrainingRepository(store),
    supplements: new SupplementsRepository(store),
    chat: new ChatRepository(store),
    settings: new SettingsRepository(store),
    cache: new CacheRepository(store)
  };
}
